// render-family-tree.ts — the "family-tree" renderer: the full World Anvil-style diagram.
//
// Drives family_tree.php. The endpoint (options/api/kin_inc.php?action=tree) ships the focus, both
// walks, every parent EDGE between the walked nodes, and every union. This file owns the layout.
//
// ⭐ NOTHING STORES THE TREE. There is no tree table and never will be — the whole graph is derived
// from parent POINTERS, which is exactly what makes it dynamic (design §4a). So this renderer is
// handed edges, not a hierarchy, and has to BUILD the generations itself.
//
// LAYOUT MODEL — generation bands, because a family is not a tree
//   A real genealogy is a DAG, not a tree: two parents converge on one child, half-siblings share
//   one parent and not the other, and the pantheon has Comes fathering nine children on six
//   different partners. Anything that assumes one parent per node collapses the moment it meets
//   real data.
//
//   So: every node is assigned a GENERATION (0 = the focus, negative = ancestors, positive =
//   descendants), each generation is a row, and edges are drawn as SVG curves between rows. A node
//   with two parents in the row above simply has two lines arriving. Couples are drawn adjacent
//   with a union bar between them.
//
// ⚠ A node's generation is its DEEPEST path from the focus, not its first. Comes is both Rohena's
//   father AND her partner, and Tyr is their child — so Comes is reachable at two different depths.
//   Taking the first path found would draw him level with his own daughter and put their child in
//   the same band as his siblings. Taking the deepest keeps every parent strictly above every child,
//   which is the one invariant a genealogy diagram must never break.

import type { CatalogListConfig, KinTreeResponse, KinNode, KinLifespan, KinTreeEdge } from './types';
import { initTooltips, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';

function el(tag: string, className = '', text = ''): HTMLElement {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
}

function nodeKey(kind: string, id: string): string {
    return `${kind}:${String(id).toUpperCase()}`;
}

function icon(id: string, w = 24, h = 24, title: string | boolean = false): string {
    return typeof iconMap === 'function' ? iconMap(id, w, h, title) : '';
}

interface Placed {
    node: KinNode;
    generation: number;
    el?: HTMLElement;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// Generation assignment
// ══════════════════════════════════════════════════════════════════════════════════════════════

// The endpoint already labels each walked node with the generation it was REACHED at (1 = the
// focus's parents / children). Ancestors sit above the focus, descendants below. Where a node turns
// up in both walks — genuinely possible in this setting, since Comes is Rohena's father and her
// partner — the DEEPEST assignment wins, so a parent is never drawn level with their own child.
function assignGenerations(data: KinTreeResponse): Map<string, Placed> {

    const placed = new Map<string, Placed>();

    const focusKey = nodeKey(data.focus.kind, data.focus.id);
    placed.set(focusKey, { node: data.focus, generation: 0 });

    // ⚠⚠ NEVER COMPARE ACROSS THE SIGN. "Deepest wins" was written as a numeric comparison, and
    // `1 > -5` is true — so a node found five generations UP and also once DOWN was moved from
    // -5 to +1, below the focus, and every parent line into it then pointed the wrong way. The
    // focus itself could be shunted off zero the same way.
    //
    // Reachability in BOTH directions through parent edges is an ancestry CYCLE — the save guard
    // refuses to author one, but a row can still arrive by import or by hand. No single band can
    // keep every parent above every child in that case, so the ANCESTOR placement wins and the
    // descendant sighting is dropped: showing someone too high is legible, showing a parent
    // beneath their own child is not.
    const consider = (node: KinNode, generation: number) => {
        const key = nodeKey(node.kind, node.id);

        // The focus is pinned. Nothing may move it.
        if (key === focusKey) return;

        const existing = placed.get(key);
        if (!existing) {
            placed.set(key, { node, generation });
            return;
        }
        if (Math.sign(generation) !== Math.sign(existing.generation)) {
            // Ancestry wins over descent — see above.
            if (generation < 0) existing.generation = generation;
            return;
        }
        // Same side: the deeper sighting wins.
        if (generation < 0 && generation < existing.generation) existing.generation = generation;
        if (generation > 0 && generation > existing.generation) existing.generation = generation;
    };

    (Array.isArray(data.ancestors) ? data.ancestors : []).forEach((n) => consider(n, -(n.generation || 1)));
    (Array.isArray(data.descendants) ? data.descendants : []).forEach((n) => consider(n, n.generation || 1));

    // A partner is not reached by either walk — Comta is nobody's ancestor in a tree focused on
    // Rhalotl, she is his mother's spouse — so unions place their unknown side beside the side we
    // already have. Without this, half of every couple in the diagram is missing.
    (Array.isArray(data.partners) ? data.partners : []).forEach((pair) => {
        const aKey = nodeKey(pair.a.kind, pair.a.id);
        const bKey = nodeKey(pair.b.kind, pair.b.id);
        const a = placed.get(aKey);
        const b = placed.get(bKey);
        if (a && !b) placed.set(bKey, { node: pair.b, generation: a.generation });
    });

    return placed;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// Band ordering
// ══════════════════════════════════════════════════════════════════════════════════════════════

// ⚠⚠ PARTNERS MUST SIT NEXT TO EACH OTHER, and this is a correctness problem, not a cosmetic one.
// A union is drawn as a bar between two cards. Leave the order to chance and that bar runs straight
// THROUGH whatever card happens to sit between them — so a band holding [mother, someone-else,
// father] renders one continuous line across all three and reads as a marriage of three people.
// Caught on staging 2026-08-20 with exactly that arrangement.
//
// So: greedily chain each band into partner runs before laying it out. A creature with several
// partners (Comes has seven) anchors a run and its partners follow, which is the best a single row
// can do; the non-adjacent leftovers are handled by routing their bar UNDER the band (drawEdges).
// ⭐ SIBLINGS GO IN BIRTH ORDER — the convention every genealogy chart uses, and the one thing that
// makes a generation band readable as a sequence rather than as a set. Applied BEFORE the partner
// clustering below, so chronology sets the seed order and couples are then kept contiguous on top
// of it; sorting afterwards would tear spouses apart again.
//
// ⚠ SORT ON birth_sort_key, NEVER ON THE YEAR. The key is SIGNED because BT counts DOWN: 300 BT is
// earlier than 91 BT, so a raw-year sort renders the entire pre-Titan half of the pantheon
// backwards. This is the same trap ft_timeline_sort_key() exists to close server-side.
//
// ⚠ UNDATED NODES KEEP THEIR INCOMING ORDER, AT THE END. Most catalog creatures have no lifespan at
// all, and a chart that reshuffled them on every load would look broken; a stable partition is the
// honest rendering of "these are dated, these are not".
function orderBandByBirth(entries: Placed[]): Placed[] {

    const dated: { entry: Placed; key: number; at: number }[] = [];
    const undated: Placed[] = [];

    entries.forEach((entry, at) => {
        const key = entry.node.life?.birth_sort_key;
        if (typeof key === 'number' && Number.isFinite(key)) {
            dated.push({ entry, key, at });
        } else {
            undated.push(entry);
        }
    });

    // Ties broken by the incoming index, so the ten Titans (all born 300 BT) stay in a stable order
    // instead of shuffling between loads.
    dated.sort((a, b) => (a.key - b.key) || (a.at - b.at));

    return [...dated.map((d) => d.entry), ...undated];
}

function orderBandByPartners(entries: Placed[], partners: KinTreeResponse['partners']): Placed[] {

    if (entries.length < 3) return entries;

    const index = new Map<string, Placed>();
    entries.forEach((e) => index.set(nodeKey(e.node.kind, e.node.id), e));

    // Adjacency limited to THIS band — a partner in another generation cannot be sat beside them.
    const links = new Map<string, string[]>();
    partners.forEach((pair) => {
        const a = nodeKey(pair.a.kind, pair.a.id);
        const b = nodeKey(pair.b.kind, pair.b.id);
        if (!index.has(a) || !index.has(b)) return;
        // Deduped: the same couple can be reported more than once across a wide payload, and a
        // repeated neighbour is pure wasted work in the walk below.
        const push = (from: string, to: string) => {
            const current = links.get(from) || [];
            if (!current.includes(to)) links.set(from, [...current, to]);
        };
        push(a, b);
        push(b, a);
    });

    if (!links.size) return entries;

    const placed = new Set<string>();
    const ordered: Placed[] = [];

    // ⚠ An explicit stack, not recursion. A long partner chain in one band recurses once per link,
    // and the server caps a tree at 300 nodes — deep enough to hand a user
    // "RangeError: Maximum call stack size exceeded" instead of a diagram.
    const emit = (start: string) => {
        const stack = [start];
        while (stack.length) {
            const key = stack.pop() as string;
            const entry = index.get(key);
            if (!entry || placed.has(key)) continue;
            placed.add(key);
            ordered.push(entry);
            // Walk this node's partners immediately, so a couple (or a cluster around one
            // much-married node) stays contiguous instead of being split by an unrelated sibling.
            // Reversed, so the popped order matches the authored order.
            const neighbours = links.get(key) || [];
            for (let i = neighbours.length - 1; i >= 0; i--) {
                const next = neighbours[i];
                if (next !== undefined && !placed.has(next)) stack.push(next);
            }
        }
    };

    // Preserve the incoming order for anything unpartnered, so the layout stays stable between loads.
    entries.forEach((entry) => emit(nodeKey(entry.node.kind, entry.node.id)));

    return ordered;
}


// ══════════════════════════════════════════════════════════════════════════════════════════════
// Cards
// ══════════════════════════════════════════════════════════════════════════════════════════════

// ⭐ A PORTRAIT ABOVE A NAME PLATE — the shape World Anvil and CK3 both use, and the reason their
// charts read as PEOPLE rather than as a graph of boxes. The art is batch-resolved server-side
// (ft_kin_portrait_map); a creature with none falls back to its creature-type icon, and a creature
// the viewer may not see gets neither — withholding the name while showing the face would defeat
// the point of hiding it.
// ⚠ AN ARTICLE NODE HAS NO item_id. Articles are the third kin kind and they are not `items` at
// all, so the item.php/encounter.php pair is not exhaustive — routing one through the encounter
// branch produced `encounter.php?id=` and a link to nowhere on every god in the pantheon.
function nodeHref(node: KinNode): string {
    if (node.kind === 'article') {
        // ⚠ PREFER THE SERVER'S URL. An article's readable address contains its slug, which this
        // bundle has no way to derive from an id — ft_kin_node_arr() resolves it and ships it on the
        // node. The id form stays as the fallback for a node resolved before that field existed.
        return node.url || `article.php?id=${encodeURIComponent(String(node.id))}`;
    }
    if (node.kind === 'item') {
        return `item.php?id=${encodeURIComponent(String(node.item_id || node.id))}`;
    }
    return `encounter.php?id=${encodeURIComponent(String(node.encounter_id || ''))}`;
}

// The one-line date string a card shows. Returns '' when there is nothing worth the row.
//
// ⚠ THE AGE IS NOT IN HERE. It is usually null (no world present year is stored), it is the widest
// part of the string when it is not, and it is the least useful of the three — so it lives in the
// tooltip and the dates get the line.
function lifeLine(life: KinLifespan | null | undefined): string {
    if (!life) return '';

    const birth = life.eternal ? '∞' : (life.birth || '');   // ∞ = always existed
    const death = life.death || '';

    if (birth && death) return `${birth} – ${death}`;
    if (birth) return life.is_dead ? `${birth} – ?` : `b. ${birth}`;
    if (death) return `d. ${death}`;
    return '';
}

function lifeTooltip(life: KinLifespan | null | undefined): string {
    if (!life) return '';

    const bits: string[] = [];
    if (life.eternal) bits.push('Always existed in the void of time');
    if (life.age !== null && life.age !== undefined) {
        bits.push(life.age_source === 'at_death' ? `${life.age} years old at death` : `${life.age} years old`);
    }
    if (life.status === 'undying') bits.push('Undying');
    // ⚠ Worth surfacing: an inherited record belongs to the CATALOG creature, not this placement, so
    // a GM editing it is about to copy it down rather than change what they are looking at.
    if (life.inherited) bits.push('Inherited from the catalog creature');
    return bits.join(' · ');
}

function buildCard(entry: Placed, isFocus: boolean): HTMLElement {

    const node = entry.node;
    const card = el('div', 'ft-kin-node');
    if (isFocus) card.classList.add('ft-kin-node--focus');
    if (node.hidden) card.classList.add('ft-kin-node--hidden');

    card.setAttribute('data-ft-kin-node', nodeKey(node.kind, node.id));
    card.setAttribute('data-ft-kin-name', (node.name || '').toLowerCase());

    const portrait = el('div', 'ft-kin-portrait');
    if (!node.hidden && node.pic_url) {
        const img = document.createElement('img');
        img.src = node.pic_url;
        img.alt = '';
        img.loading = 'lazy';
        portrait.appendChild(img);
    } else {
        // No art (or nothing this viewer may see): the creature-type glyph keeps the node the same
        // size and shape, so a partly-illustrated family still lays out as one row of faces.
        portrait.classList.add('ft-kin-portrait--placeholder');
        portrait.innerHTML = icon(node.hidden ? 'encounter_creature_private' : 'creature', 26, 26, false);
    }
    card.appendChild(portrait);

    const plate = el('div', 'ft-kin-plate');

    if (node.hidden) {
        plate.appendChild(el('span', 'ft-kin-node-name fst-italic', 'Unknown'));
        card.setAttribute('data-bs-toggle', 'tooltip');
        card.setAttribute('data-bs-original-title', 'This creature is not public.');
        card.appendChild(plate);
        return card;
    }

    const link = document.createElement('a');
    link.className = 'ft-kin-node-name';
    link.textContent = node.name || 'Unnamed';
    link.href = nodeHref(node);
    plate.appendChild(link);

    // ⭐ DATES UNDER THE NAME — what turns a graph of names into a chronology you can read
    // generations off. Deliberately terse ("91 BT – 62 BT"), because a card is ~120px wide and a
    // sentence would wrap into the row below it.
    const dates = lifeLine(node.life);
    if (dates) {
        plate.appendChild(el('span', 'ft-kin-node-dates', dates));
    }

    card.appendChild(plate);

    // ⭐ THE DEAD MARKER GOES ON THE PORTRAIT, not in the text. "Nine of the ten Titans fell" should
    // be legible as a PATTERN at a glance across a generation band — a word in a date line is not,
    // a dimmed face with a skull is.
    if (node.life?.is_dead) {
        card.classList.add('ft-kin-node--dead');
        const marker = el('span', 'ft-kin-dead-marker', '☠');
        marker.setAttribute('aria-label', 'Dead');
        portrait.appendChild(marker);
    }

    // The age is a hover detail, never a second line — see lifeLine().
    const tip = lifeTooltip(node.life);
    if (tip) {
        plate.setAttribute('data-bs-toggle', 'tooltip');
        plate.setAttribute('data-bs-original-title', tip);
    }

    // Re-centring the diagram on another creature is the single most useful thing this page does —
    // it is how you walk a pantheon rather than stare at one branch of it.
    if (!isFocus) {
        const recentre = document.createElement('a');
        recentre.className = 'ft-kin-node-recentre';
        recentre.href = `family_tree.php?kind=${encodeURIComponent(node.kind)}&id=${encodeURIComponent(String(node.id))}`;
        recentre.textContent = '⌖';
        recentre.setAttribute('data-bs-toggle', 'tooltip');
        recentre.setAttribute('data-bs-original-title', 'Centre the tree here');
        card.appendChild(recentre);
    }

    return card;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// Edges
// ══════════════════════════════════════════════════════════════════════════════════════════════

// Drawn AFTER layout, from real measured positions, because the bands are flex rows whose widths
// depend on content. An SVG overlay sized to the diagram keeps the lines correct at any zoom and
// lets a disputed edge be dashed without touching the DOM structure.
function drawEdges(
    surface: HTMLElement,
    svg: SVGSVGElement,
    edges: KinTreeEdge[],
    partners: KinTreeResponse['partners'],
    scale = 1,
): void {

    while (svg.firstChild) svg.removeChild(svg.firstChild);

    // ⚠ EVERY MEASUREMENT IS DIVIDED BY THE ZOOM. The surface carries a CSS transform, so
    // getBoundingClientRect() returns SCALED pixels while the SVG's viewBox is in the surface's own
    // unscaled coordinate space. Mixing the two draws every line at the wrong length the moment
    // anybody touches the zoom control.
    const z = scale || 1;
    const box = surface.getBoundingClientRect();
    // ⚠ viewBox ONLY. Setting width/height ATTRIBUTES made the overlay able to overflow the
    // scroll container, which toggled a scrollbar, which changed the surface size, which fired the
    // ResizeObserver, which redrew — an infinite resize loop that pinned the CPU and left the
    // element permanently "not stable" (caught 2026-08-21 when Playwright could not screenshot it).
    // The CSS already sizes the overlay at 100%/100%; user units follow the viewBox.
    svg.setAttribute('viewBox', `0 0 ${Math.max(1, box.width / z)} ${Math.max(1, box.height / z)}`);

    const centreOf = (key: string): { x: number; y: number; top: number; bottom: number; left: number; right: number } | null => {
        const node = surface.querySelector<HTMLElement>(`[data-ft-kin-node="${CSS.escape(key)}"]`);
        if (!node) return null;
        const r = node.getBoundingClientRect();
        return {
            x: (r.left - box.left + r.width / 2) / z,
            y: (r.top - box.top + r.height / 2) / z,
            top: (r.top - box.top) / z,
            bottom: (r.bottom - box.top) / z,
            left: (r.left - box.left) / z,
            right: (r.right - box.left) / z,
        };
    };

    const line = (from: { x: number; y: number }, to: { x: number; y: number }, className: string) => {
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        // A cubic with vertical control handles reads as a family-tree bracket rather than a
        // scribble, and stays legible when two parents converge on one child.
        const midY = (from.y + to.y) / 2;
        path.setAttribute('d', `M ${from.x} ${from.y} C ${from.x} ${midY}, ${to.x} ${midY}, ${to.x} ${to.y}`);
        path.setAttribute('class', className);
        path.setAttribute('fill', 'none');
        svg.appendChild(path);
    };

    // ══════════════════════════════════════════════════════════════════════════════════════════
    // Descent — the T/Y join
    // ══════════════════════════════════════════════════════════════════════════════════════════
    // ⭐ A CHILD OF A COUPLE HANGS OFF THE UNION, NOT OFF EACH PARENT SEPARATELY. This is the
    // convention every genealogy chart uses, and it is not decoration: two independent curves
    // converging on one child say "two unrelated people each contributed a child", while a single
    // stem dropping from the marriage bar says "this couple had this child" — which is the fact.
    // With several children the stem also gathers them, so a family reads as a family.
    //
    //   Bedaview ——— Bogeyman      the union bar
    //            │                 the STEM, from the bar's midpoint
    //       ┌────┴────┐            the junction
    //       │         │
    //     child     child
    //
    // A parent who is NOT part of the couple keeps their own line — which is exactly right for a
    // DISPUTED alternative parent: a competing claim is not part of the established marriage, and
    // folding it into the couple's stem would assert something the record does not.

    const draw = (d: string, className: string) => {
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', d);
        path.setAttribute('class', className);
        path.setAttribute('fill', 'none');
        svg.appendChild(path);
    };

    // ⭐ ORTHOGONAL, NOT CURVED. A family chart is read structurally — "these are the children of
    // that union" — and right angles state that; a bundle of curves fanning from one point reads as
    // a spray and leaves the eye to guess where each line began. Rounded corners keep it from
    // looking like a circuit diagram.
    const R = 8;
    const elbow = (from: { x: number; y: number }, to: { x: number; y: number }, className: string) => {
        if (Math.abs(to.x - from.x) < 1) {
            draw(`M ${from.x} ${from.y} L ${to.x} ${to.y}`, className);
            return;
        }
        const midY = (from.y + to.y) / 2;
        const dir = to.x > from.x ? 1 : -1;
        const r = Math.min(R, Math.abs(to.x - from.x) / 2, Math.abs(midY - from.y), Math.abs(to.y - midY));
        draw(
            `M ${from.x} ${from.y}`
            + ` L ${from.x} ${midY - r}`
            + ` Q ${from.x} ${midY} ${from.x + dir * r} ${midY}`
            + ` L ${to.x - dir * r} ${midY}`
            + ` Q ${to.x} ${midY} ${to.x} ${midY + r}`
            + ` L ${to.x} ${to.y}`,
            className,
        );
    };

    // Who is partnered with whom, as an unordered pair lookup.
    const pairKey = (a: string, b: string) => (a < b ? `${a}||${b}` : `${b}||${a}`);
    const unionPairs = new Set<string>();
    partners.forEach((pair) => {
        unionPairs.add(pairKey(nodeKey(pair.a.kind, pair.a.id), nodeKey(pair.b.kind, pair.b.id)));
    });

    // Every parent edge, grouped by the child it points at.
    const edgesByChild = new Map<string, KinTreeEdge[]>();
    edges.forEach((edge) => {
        const key = nodeKey(edge.child.kind, edge.child.id);
        edgesByChild.set(key, [...(edgesByChild.get(key) || []), edge]);
    });

    // Couples that actually have children together, and which children those are. Keyed by the
    // couple so several siblings share ONE stem instead of each drawing their own.
    interface Brood { aKey: string; bKey: string; children: string[]; inherited: boolean }
    const broods = new Map<string, Brood>();
    const handled = new Set<string>(); // "childKey||parentKey" edges drawn as part of a couple

    edgesByChild.forEach((childEdges, childKey) => {

        // ⚠ DISPUTED EDGES NEVER FORM A COUPLE. They are alternative claims, so they must stay
        // separate lines — see the block comment above.
        const settled = childEdges.filter((e) => !e.parent.disputed);
        if (settled.length < 2) return;

        for (let i = 0; i < settled.length; i++) {
            for (let j = i + 1; j < settled.length; j++) {
                const first = settled[i];
                const second = settled[j];
                if (!first || !second) continue;

                const aKey = nodeKey(first.parent.kind, first.parent.id);
                const bKey = nodeKey(second.parent.kind, second.parent.id);
                if (!unionPairs.has(pairKey(aKey, bKey))) continue;

                const key = pairKey(aKey, bKey);
                const brood = broods.get(key) || {
                    aKey,
                    bKey,
                    children: [],
                    inherited: false,
                };
                if (!brood.children.includes(childKey)) brood.children.push(childKey);
                if (first.parent.inherited || second.parent.inherited) brood.inherited = true;
                broods.set(key, brood);

                handled.add(`${childKey}||${aKey}`);
                handled.add(`${childKey}||${bKey}`);

                // One couple per child is enough — a second marriage among the same child's parents
                // would be the same two people.
                i = settled.length;
                break;
            }
        }
    });

    broods.forEach((brood) => {
        const a = centreOf(brood.aKey);
        const b = centreOf(brood.bKey);
        if (!a || !b) return;

        const kids = brood.children
            .map((key) => centreOf(key))
            .filter((c): c is NonNullable<typeof c> => c !== null);
        if (!kids.length) return;

        const classes = ['ft-kin-edge'];
        if (brood.inherited) classes.push('ft-kin-edge--inherited');

        // The stem starts at the MIDPOINT OF THE MARRIAGE BAR — the point that means "this couple".
        const originX = (a.x + b.x) / 2;
        const parentsBottom = Math.max(a.bottom, b.bottom);

        // The sibling bar sits between the parents and their children, so the stem, the bar and the
        // drops are each visible as their own segment. That separation IS the readability.
        const childTop = Math.min(...kids.map((c) => c.top));
        const barGap = Math.max(14, (childTop - parentsBottom) * 0.45);
        const junctionY = parentsBottom + barGap;

        const cls = classes.join(' ');

        // ⭐ 1. THE COUPLE BRACKET — World Anvil's shape, and better than a bar drawn between the two
        //    cards: a short stub drops from EACH parent into a joining bar, and the stem descends
        //    from that bar's centre. It stays correct however far apart the couple sit, which a
        //    between-the-cards bar does not — someone with several partners cannot be adjacent to
        //    all of them.
        const bracketY = parentsBottom + Math.max(8, barGap * 0.4);
        draw(`M ${a.x} ${a.bottom} L ${a.x} ${bracketY}`, cls);
        draw(`M ${b.x} ${b.bottom} L ${b.x} ${bracketY}`, cls);
        draw(`M ${Math.min(a.x, b.x)} ${bracketY} L ${Math.max(a.x, b.x)} ${bracketY}`, cls);
        draw(`M ${originX} ${bracketY} L ${originX} ${junctionY}`, cls);

        // 2. THE SIBLING BAR — horizontal, spanning the children. This is what makes a brood read as
        //    siblings of each other and not as three unrelated descents that happen to share a point.
        const xs = kids.map((c) => c.x);
        const leftX = Math.min(...xs, originX);
        const rightX = Math.max(...xs, originX);
        if (rightX - leftX > 1) {
            draw(`M ${leftX} ${junctionY} L ${rightX} ${junctionY}`, cls);
        }

        // 3. THE DROPS — a short vertical from the bar into each child.
        kids.forEach((child) => {
            draw(`M ${child.x} ${junctionY} L ${child.x} ${child.top}`, cls);
        });
    });

    // Everything the couple pass did not draw: single parents, and every disputed claim.
    edges.forEach((edge) => {
        const childKey = nodeKey(edge.child.kind, edge.child.id);
        const parentKey = nodeKey(edge.parent.kind, edge.parent.id);
        if (handled.has(`${childKey}||${parentKey}`)) return;

        const child = centreOf(childKey);
        const parent = centreOf(parentKey);
        if (!child || !parent) return;

        // Disputed edges are DASHED, not hidden and not flagged as errors — doc 02 preserves two
        // mothers for Ivona on purpose and the diagram has to show both claims.
        const classes = ['ft-kin-edge'];
        if (edge.parent.disputed) classes.push('ft-kin-edge--disputed');
        if (edge.parent.inherited) classes.push('ft-kin-edge--inherited');

        elbow(
            { x: parent.x, y: parent.bottom },
            { x: child.x, y: child.top },
            classes.join(' '),
        );
    });

    partners.forEach((pair) => {
        const aKey = nodeKey(pair.a.kind, pair.a.id);
        const bKey = nodeKey(pair.b.kind, pair.b.id);

        // ⚠ A couple WITH children is already joined by their bracket — drawing the between-cards
        // bar as well doubles the marriage and clutters the gap. A CHILDLESS couple still needs it,
        // or their union would not appear at all.
        if (broods.has(pairKey(aKey, bKey))) return;

        const a = centreOf(aKey);
        const b = centreOf(bKey);
        if (!a || !b) return;

        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

        // ⚠ A straight chord between two cards is only safe when NOTHING sits between them.
        // orderBandByPartners() puts couples side by side so that is the usual case — but a node
        // with several partners cannot have all of them adjacent, and drawing that bar straight
        // would run it through an unrelated creature and read as a marriage of three.
        // So: measure the gap. Adjacent → a short straight bar. Otherwise → dip the line BELOW the
        // band, where it passes under the intervening cards instead of across them.
        const gap = Math.abs(b.x - a.x);
        const widest = Math.max(a.right - a.left, b.right - b.left);
        const adjacent = gap <= widest * 1.6;

        if (adjacent) {
            // ⚠ EDGE TO EDGE, not centre to centre. A centre-anchored bar is drawn straight through
            // both cards' interiors and strikes a line through their names. Anchor it on the facing
            // edges so it occupies only the gap between them, which is the bit that means "married".
            const [leftCard, rightCard] = a.x <= b.x ? [a, b] : [b, a];
            path.setAttribute('d', `M ${leftCard.right} ${leftCard.y} L ${rightCard.left} ${rightCard.y}`);
        } else {
            const dip = Math.max(a.bottom, b.bottom) + 14;
            path.setAttribute(
                'd',
                `M ${a.x} ${a.bottom} C ${a.x} ${dip}, ${b.x} ${dip}, ${b.x} ${b.bottom}`,
            );
        }

        path.setAttribute('class', 'ft-kin-edge ft-kin-edge--union');
        path.setAttribute('fill', 'none');
        svg.appendChild(path);
    });
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// Drag to pan
// ══════════════════════════════════════════════════════════════════════════════════════════════

// Grab the empty space and drag the diagram around inside its frame, the way every map and family
// tree behaves. A real genealogy outgrows any viewport — the pantheon has a generation of nine — and
// hunting for a scrollbar to see a cousin is not a thing a reader should have to do.
//
// ⚠ IT MUST NOT EAT CLICKS. Two guards, and both are needed:
//   1. A pointerdown that starts on something INTERACTIVE (a name link, the re-centre control, a
//      button, a form field) is left alone entirely — those elements own their gestures.
//   2. Everywhere else, panning only ENGAGES after the pointer has moved past a small threshold. A
//      plain click on a portrait therefore still reaches the card; only a genuine drag pans.
//
// ⚠ MOUSE ONLY, on purpose. A touch device already pans a scroll container natively and far better
// than this would; hijacking it (via `touch-action: none`) would replace momentum scrolling with a
// worse imitation. Pen and touch fall through to the browser.
function attachDragPan(frame: HTMLElement): void {

    const INTERACTIVE = 'a, button, input, select, textarea, label, [role="button"], [data-ft-ld-action]';
    const THRESHOLD = 4; // px of movement before a press becomes a drag

    let pointerId: number | null = null;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;
    let panning = false;

    const stop = () => {
        if (pointerId !== null) {
            try { frame.releasePointerCapture(pointerId); } catch { /* already released */ }
        }
        pointerId = null;
        panning = false;
        frame.classList.remove('ft-kin-panning');
    };

    frame.addEventListener('pointerdown', (event: PointerEvent) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target instanceof Element && event.target.closest(INTERACTIVE)) return;

        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        startLeft = frame.scrollLeft;
        startTop = frame.scrollTop;
        panning = false;
    });

    frame.addEventListener('pointermove', (event: PointerEvent) => {
        if (pointerId === null || event.pointerId !== pointerId) return;

        const dx = event.clientX - startX;
        const dy = event.clientY - startY;

        if (!panning) {
            if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return;
            // Past the threshold: this is a drag, not a click. Capture so the gesture survives the
            // pointer leaving the frame, and flag it for the cursor + text-selection styling.
            panning = true;
            frame.classList.add('ft-kin-panning');
            try { frame.setPointerCapture(pointerId); } catch { /* not capturable */ }
        }

        // Drag the CONTENT with the pointer: moving right reveals what is to the left.
        frame.scrollLeft = startLeft - dx;
        frame.scrollTop = startTop - dy;
        event.preventDefault();
    });

    for (const done of ['pointerup', 'pointercancel', 'pointerleave'] as const) {
        frame.addEventListener(done, stop);
    }

    // A drag that ends over a card would otherwise fire that card's click on release.
    frame.addEventListener('click', (event) => {
        if (frame.classList.contains('ft-kin-panning')) {
            event.preventDefault();
            event.stopPropagation();
        }
    }, true);
}


// ══════════════════════════════════════════════════════════════════════════════════════════════
// Mount
// ══════════════════════════════════════════════════════════════════════════════════════════════

export function mountCatalogFamilyTree(root: HTMLElement, config: CatalogListConfig): void {

    const container = results(root);
    if (!container) return;

    const kind = root.getAttribute('data-ft-kin-kind') || 'item';
    const id = root.getAttribute('data-ft-kin-id') || '';
    const emptyText = root.getAttribute('data-ft-empty-text') || 'No family recorded for this creature yet.';

    if (!id) {
        setStatus(root, 'No creature specified.');
        return;
    }

    const build = (data: KinTreeResponse): void => {

        container.textContent = '';

        const placed = assignGenerations(data);

        if (placed.size <= 1) {
            container.appendChild(el('div', 'text-muted text-center py-4', emptyText));
            return;
        }

        // Group into bands, oldest generation first.
        const byGeneration = new Map<number, Placed[]>();
        placed.forEach((entry) => {
            const list = byGeneration.get(entry.generation) || [];
            list.push(entry);
            byGeneration.set(entry.generation, list);
        });

        const surface = el('div', 'ft-kin-tree');
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('class', 'ft-kin-tree-edges');
        surface.appendChild(svg);

        const focusKey = nodeKey(data.focus.kind, data.focus.id);

        Array.from(byGeneration.keys()).sort((a, b) => a - b).forEach((generation) => {
            const band = el('div', 'ft-kin-band');

            // ⭐ NO BAND CAPTIONS AT ALL — World Anvil stamps none, and neither do we (owner,
            // 2026-08-21). The bands themselves say which generation is which, the focus already
            // wears its gold ring, and a caption per row only competes with the lines that carry
            // the structure, since they cross that row on their way between bands.

            const row = el('div', 'ft-kin-band-row');
            orderBandByPartners(
                orderBandByBirth(byGeneration.get(generation) || []),
                Array.isArray(data.partners) ? data.partners : [],
            ).forEach((entry) => {
                const card = buildCard(entry, nodeKey(entry.node.kind, entry.node.id) === focusKey);
                entry.el = card;
                row.appendChild(card);
            });
            band.appendChild(row);
            surface.appendChild(band);
        });

        container.appendChild(surface);

        // Grab-and-drag the diagram inside its frame. Attached once per mount; the guard keeps it
        // idempotent if the renderer ever rebuilds in place.
        if (container.getAttribute('data-ft-kin-pan') !== '1') {
            container.setAttribute('data-ft-kin-pan', '1');
            attachDragPan(container);
        }

        // ══════════════════════════════════════════════════════════════════════════════════════
        // Controls — search + zoom, bottom-left, the way World Anvil does it
        // ══════════════════════════════════════════════════════════════════════════════════════
        // Both exist for the same reason: a real genealogy outgrows the viewport. The pantheon has
        // a generation of nine siblings, and finding one by eye in a row that scrolls off both
        // edges is not a thing a reader should have to do.
        let scale = 1;

        const applyScale = () => {
            surface.style.transformOrigin = 'top center';
            surface.style.transform = scale === 1 ? '' : `scale(${scale})`;
            // ⚠ A CSS transform does NOT change layout. Zoomed out, the surface still RESERVES its
            // full-size box, so the page kept a screen of blank space between a shrunken diagram and
            // the controls below it. Reclaim the difference with a negative margin — offsetHeight is
            // the unscaled layout height, and with transform-origin at the top the leftover is
            // exactly height × (1 − scale).
            surface.style.marginBottom = scale === 1
                ? ''
                : `${-Math.round(surface.offsetHeight * (1 - scale))}px`;
            scheduleRedraw();
        };

        // ══════════════════════════════════════════════════════════════════════════════════════
        // Wheel to zoom
        // ══════════════════════════════════════════════════════════════════════════════════════
        // Mouse wheel over the frame zooms, the way every map widget behaves. Shares the SAME
        // `scale` and clamps as the +/- buttons, so the two controls can never disagree.
        //
        // ⚠ IT SWALLOWS PAGE SCROLL, and that has to be handled honestly. A reader scrolling the
        // page with the cursor over a big diagram would otherwise be TRAPPED in it. So: once the
        // zoom is already pinned at a clamp bound and the wheel keeps pushing that way, the event
        // is NOT consumed and the page scrolls through normally. Inside the range it is consumed,
        // which is what makes the zoom feel like it owns the gesture.
        //
        // ⚠ `{ passive: false }` is required — preventDefault() on a passive wheel listener is
        // ignored, and the page would scroll AND zoom at the same time.
        //
        // ⚠ Coalesced into one animation frame: a trackpad emits a stream of small deltas, and
        // redrawing every SVG edge per event would drop frames on a large tree.
        let wheelQueued = 0;
        const ZOOM_MIN = 0.4;
        const ZOOM_MAX = 2;

        container.addEventListener('wheel', (event: WheelEvent) => {

            // Leave the browser's own zoom/scroll alone when a modifier is held.
            if (event.ctrlKey || event.metaKey) return;

            const zoomingIn = event.deltaY < 0;
            const atBound = zoomingIn ? scale >= ZOOM_MAX : scale <= ZOOM_MIN;
            if (atBound) return; // let the page have the scroll rather than trapping the reader

            event.preventDefault();

            // Sign only, not magnitude: a mouse notch and a trackpad flick report wildly different
            // deltaY, and using it raw makes a trackpad leap the whole range in one gesture.
            const next = Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale + (zoomingIn ? 0.1 : -0.1))) * 100) / 100;
            if (next === scale) return;
            scale = next;

            if (wheelQueued) return;
            wheelQueued = window.requestAnimationFrame(() => {
                wheelQueued = 0;
                applyScale();
            });
        }, { passive: false });

        const controls = el('div', 'ft-kin-controls d-flex align-items-center gap-2');

        // ⭐ BIND THE PAGE'S OWN SEARCH FIELD when it provided one (data-ft-search-input-id, the
        // same contract every other catalog-list page uses). That field is server-rendered with the
        // shared spyglass/clear chrome, so it looks and behaves like search everywhere else on the
        // site. Only build a bare fallback input when no page field exists — which is what keeps
        // this renderer usable on a host page that has no toolbar.
        const pageSearch = config.searchInputId
            ? document.getElementById(config.searchInputId) as HTMLInputElement | null
            : null;

        const search = pageSearch ?? document.createElement('input');
        if (!pageSearch) {
            search.type = 'search';
            search.className = 'form-control form-control-sm ft-kin-search flex-grow-1';
            search.placeholder = 'Search family tree';
            search.setAttribute('autocomplete', 'off');
        }
        search.addEventListener('input', () => {
            const term = search.value.trim().toLowerCase();
            // Dim rather than hide: removing non-matches would collapse the bands and destroy the
            // very structure the reader is searching within.
            surface.querySelectorAll<HTMLElement>('[data-ft-kin-node]').forEach((cardEl) => {
                const name = cardEl.getAttribute('data-ft-kin-name') || '';
                const hit = term === '' || name.includes(term);
                cardEl.classList.toggle('ft-kin-node--dim', !hit);
                cardEl.classList.toggle('ft-kin-node--hit', term !== '' && hit);
            });
        });
        // Only adopt the input into the control strip when we made it; the page's own field already
        // sits where the page put it.
        if (!pageSearch) {
            controls.appendChild(search);
        }

        const zoomBtn = (label: string, title: string, delta: number) => {
            const btn = el('button', 'btn btn-sm btn-outline-secondary ft-kin-zoom-btn', label);
            btn.setAttribute('type', 'button');
            btn.setAttribute('aria-label', title);
            btn.setAttribute('data-bs-toggle', 'tooltip');
            btn.setAttribute('data-bs-original-title', title);
            btn.addEventListener('click', () => {
                // Clamped: below ~0.4 the name plates stop being readable and the diagram becomes
                // decoration; above 2 a single card fills the container. Same bounds the wheel uses.
                scale = Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale + delta)) * 100) / 100;
                applyScale();
            });
            return btn;
        };
        controls.appendChild(zoomBtn('−', 'Zoom out', -0.15));
        controls.appendChild(zoomBtn('+', 'Zoom in', 0.15));

        const reset = el('button', 'btn btn-sm btn-outline-secondary ft-kin-zoom-reset', 'Reset');
        reset.setAttribute('type', 'button');
        reset.addEventListener('click', () => {
            scale = 1;
            search.value = '';
            search.dispatchEvent(new Event('input'));
            applyScale();
        });
        controls.appendChild(reset);

        // Prefer the page toolbar slot; fall back to below the diagram so the component still works
        // anywhere the host page has not provided one.
        const slot = document.querySelector('[data-ft-kin-controls-slot]');
        if (slot) {
            slot.textContent = '';
            slot.appendChild(controls);
        } else {
            container.appendChild(controls);
        }

        // ⚠ Say so when the server truncated the walk. A partial family drawn without a word looks
        // exactly like a complete one, and the reader has no way to tell they are missing branches.
        if (data.truncated === 1) {
            container.appendChild(el(
                'div',
                'alert alert-info py-1 px-2 small mt-2',
                'This family is larger than one diagram can show — some branches are not drawn. '
                + 'Centre the tree on a relative to explore further.',
            ));
        }

        // Edges are measured from the laid-out DOM, so they must be drawn after a paint — and
        // redrawn whenever the surface resizes, or every line points at where a card used to be.
        const redraw = () => drawEdges(
            surface,
            svg,
            Array.isArray(data.edges) ? data.edges : [],
            Array.isArray(data.partners) ? data.partners : [],
            scale,
        );

        // ⚠ A ResizeObserver whose callback touches the observed subtree is one careless line away
        // from an infinite loop. Two guards, and keep both: redraw only when the measured size has
        // ACTUALLY changed, and coalesce into a single animation frame so a burst of observations
        // costs one pass. Without the size check the observer can feed itself forever.
        let lastW = -1;
        let lastH = -1;
        let queued = 0;

        const scheduleRedraw = () => {
            if (queued) return;
            queued = window.requestAnimationFrame(() => {
                queued = 0;
                redraw();
            });
        };

        window.requestAnimationFrame(() => {
            const box = surface.getBoundingClientRect();
            lastW = box.width;
            lastH = box.height;
            redraw();
        });

        if (typeof ResizeObserver === 'function') {
            new ResizeObserver(() => {
                const box = surface.getBoundingClientRect();
                if (Math.abs(box.width - lastW) < 0.5 && Math.abs(box.height - lastH) < 0.5) return;
                lastW = box.width;
                lastH = box.height;
                scheduleRedraw();
            }).observe(surface);
        } else {
            window.addEventListener('resize', scheduleRedraw);
        }

        initTooltips(container);
    };

    const load = async (): Promise<void> => {
        try {
            const params = new URLSearchParams({ action: 'tree', kind, id });
            const res = await fetch(`${resolveUrl(config.endpoint || 'options/api/kin_inc.php')}?${params.toString()}`, {
                credentials: 'same-origin',
            });
            const data: KinTreeResponse = await res.json().catch(() => ({} as any));
            // A 200 carrying `{}` is not a success — without a focus the layout has no origin and
            // assignGenerations() throws on data.focus.kind.
            if (!res.ok || (data as any).error || (data as any).ok !== true || !data.focus) {
                throw new Error((data as any).error || `HTTP ${res.status}`);
            }
            hideLoader(root);
            setStatus(root, '');
            build(data);
        } catch (e) {
            hideLoader(root);
            console.error('[family-tree] fetch/render failed', e);
            setStatus(root, 'Could not load this family tree.');
        }
    };

    showLoader(root, config);
    void load();
}
